// GalleryHero — Main photo + 4 thumbnails (2×2 grid) for HotelDetailPage.
// Clicking a thumbnail swaps the main image.

import { useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';

interface GalleryHeroProps {
  imgs: string[];
  alt: string;
  onViewAll?: () => void;
}

export function GalleryHero({ imgs, alt, onViewAll }: GalleryHeroProps) {
  const [main, setMain] = useState(0);
  const isMobile = useIsMobile();

  const displayed = imgs.slice(0, 5);
  const thumbs = imgs.slice(1, 5); // up to 4 thumbs

  return (
    <div
      style={{
        display: 'grid',
        // Mobile: full-bleed main photo, thumbs as a horizontal strip below.
        // Desktop: 2-col + 2-row gallery from the original Voyo design.
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gridTemplateRows: isMobile ? 'auto auto' : '240px 240px',
        gap: 8,
        borderRadius: 26,
        overflow: 'hidden',
        height: isMobile ? 'auto' : 488,
      }}
    >
      {/* Main image */}
      <div
        style={{
          gridRow: '1 / 3',
          position: 'relative',
          overflow: 'hidden',
          background: '#0A0F1C',
        }}
      >
        <img
          src={displayed[main] ?? displayed[0]}
          alt={alt}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'opacity 300ms ease',
          }}
          loading="eager"
        />
      </div>

      {/* Thumbnails */}
      {thumbs.map((src, i) => {
        const isLast = i === 3 && imgs.length > 5;
        return (
          <div
            key={i}
            onClick={() => setMain(i + 1)}
            style={{
              position: 'relative', overflow: 'hidden',
              cursor: 'pointer',
              background: '#0A0F1C',
              outline: main === i + 1 ? '2px solid #FF5A3C' : 'none',
              outlineOffset: -2,
            }}
          >
            <img
              src={src}
              alt={`${alt} ${i + 2}`}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transition: 'transform 400ms ease',
              }}
              loading="lazy"
              onMouseEnter={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)'; }}
            />
            {isLast && (
              <div
                onClick={e => { e.stopPropagation(); onViewAll?.(); }}
                style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
                  fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer',
                }}
              >
                + {imgs.length - 5} photos
              </div>
            )}
          </div>
        );
      })}

      {/* Pad empty cells if fewer than 4 thumbs */}
      {thumbs.length < 4 && Array.from({ length: 4 - thumbs.length }).map((_, i) => (
        <div key={`pad-${i}`} style={{ background: 'rgba(26,15,8,0.05)' }}/>
      ))}
    </div>
  );
}
